import React, { useEffect, useState, useRef, useCallback, useMemo, memo, Suspense } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import MainPageComponent from '../components/MainPage';
import { getInitialNumberKeyState } from '../utils/numberKeyUtils';
import { convertNumberKeysToVBIDs, filterDuplicateVBIDs, formatTimecode } from '../utils/vbidUtils';
import { extractYouTubeId } from '../utils/vbidIO';
import { vbidsToNumberKeys } from '../utils/numberKeyUtils';
import { useMainPageNumberKeys } from '../hooks/useMainPageNumberKeys';
import VbidDialog from '../components/VbidDialog';
import { NumberKeyState, VBID, ShareableVBID } from '../types';
import { playButtonSound } from '../utils/soundUtils';
import { getSnapshotIdByShortCode } from '../services/shortUrlService';
import { getVBIDSnapshot } from '../services/vbidSnapshotService';
import { VBIDSnapshot } from '../types/vbidSnapshot';
import ShareDialog from '../components/ShareDialog';
import { Button } from '@mui/material';
import { Share as ShareIcon } from '@mui/icons-material';
import { serverTimestamp, Timestamp } from 'firebase/firestore';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase'; // 請依你的專案實際路徑調整
import AuthDialog from '../components/AuthDialog';
import RegisterDialog from '../components/RegisterDialog';
import WelcomeDialog from '../components/WelcomeDialog';
import { useAuth } from '../contexts/AuthContext';
import { useGuestId } from '../contexts/GuestIdContext';
import { ErrorBoundary } from 'react-error-boundary';
import { CircularProgress } from '@mui/material';
import BottomBar from '../components/BottomBar';
import { useTheme, useMediaQuery } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import GroupsIcon from '@mui/icons-material/Groups';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import QRCodeDialog from '../components/QRCodeDialog';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import HomeIcon from '@mui/icons-material/Home';
import StarIcon from '@mui/icons-material/Star';
import PublicIcon from '@mui/icons-material/Public';
import GroupIcon from '@mui/icons-material/Group';
import NoteIcon from '@mui/icons-material/Note';
import HistoryIcon from '@mui/icons-material/History';
import QrCodeIcon from '@mui/icons-material/QrCode';
import { PlayArrow as PlayArrowIcon } from '@mui/icons-material';
import NumberKeyPanel from '../components/NumberKeyPanel';
import UnifiedShareDialog from '../components/UnifiedShareDialog';
import NoteRecordsDialog from '../components/NoteRecordsDialog';

declare global {
  interface Window {
    __checkplay_autoplay_flag?: boolean;
  }
}

// 🔧 修復：移除渲染期間的 console.log，避免性能問題
// console.log('MainPage.tsx loaded, useParams:', useParams());

const MainPage: React.FC = () => {
  // 🔧 修復：移除渲染期間的 console.log，避免性能問題
  // console.log('🚀 MainPage function started - PAGES VERSION');
  // console.log('🔍 當前 URL:', window.location.href);
  // console.log('🔍 當前路徑:', window.location.pathname);
  // console.log('🔍 環境:', import.meta.env.MODE);
  
  // Google API 診斷
  // console.log('🔍 Google API 診斷:');
  // console.log('  VITE_GOOGLE_API_KEY:', import.meta.env.VITE_GOOGLE_API_KEY ? '✅ 已設定' : '❌ 未設定');
  // console.log('  VITE_GOOGLE_CLIENT_ID:', import.meta.env.VITE_GOOGLE_CLIENT_ID ? '✅ 已設定' : '❌ 未設定');
  // console.log('  window.gapi:', typeof window.gapi !== 'undefined' ? '✅ 已載入' : '❌ 未載入');
  
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isRestoringSnapshot = useRef(false);
  const snapshotKey = 'mainPageSnapshot';
  const mountCount = useRef(0);
  const [searchParams] = useSearchParams();
  const queryId = searchParams.get('id');
  const { id: shareId, shortCode } = useParams();
  // SSR 跳轉支援：優先用 query string 的 id，其次用 path parameter
  // 🔧 修復：對於 /main/share/:id 路由，shareId 就是 mainId
  const mainId = queryId || shareId || shortCode;
  
  // 🔧 新增：處理從 OpenPage 傳遞的狀態
  const openPageState = location.state;
  // 🔧 修復：移除渲染期間的 console.log，避免性能問題
  // console.log('【MainPage 頁面取得的 mainId (queryId > shareId)】', mainId);
  // console.log('【Debug】queryId:', queryId, 'shareId:', shareId, 'mainId:', mainId);
  // console.log('【Debug】location.pathname:', location.pathname);
  // console.log('【Debug】location.search:', location.search);

  // 狀態定義
  const [currentMode, setCurrentMode] = useState('YouTube');
  const [videoUrl, setVideoUrl] = useState('');
  const [numberKeys, setNumberKeys] = useState<Record<string, NumberKeyState>>(getInitialNumberKeyState());
  const [abPoints, setAbPoints] = useState({
    pointA: { key: null, timestamp: null },
    pointB: { key: null, timestamp: null }
  });
  const [currentVbids, setCurrentVbids] = useState<any[]>([]);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [played, setPlayed] = useState(0);
  const [loaded, setLoaded] = useState(0);
  const [seeking, setSeeking] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [controlsTimeout, setControlsTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showNumberKeyPanel, setShowNumberKeyPanel] = useState(false);
  const [showABPointPanel, setShowABPointPanel] = useState(false);
  const [showVBIDPanel, setShowVBIDPanel] = useState(false);
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [selectedFontSize, setSelectedFontSize] = useState(24);
  const [lineWidthMap, setLineWidthMap] = useState<Record<string, number>>({});
  const [history, setHistory] = useState<any[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [vbidDialogOpen, setVbidDialogOpen] = useState(false);
  const [isBookmarkEnabled, setIsBookmarkEnabled] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [tabValue, setTabValue] = useState<number>(3);
  const [tabVbids, setTabVbids] = useState<Record<number, any[]>>({});
  const [vbids, setVbids] = useState<any[]>([]);
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [shareStep, setShareStep] = useState('input');
  const [currentShareId, setCurrentShareId] = useState('');
  const [currentShareUrl, setCurrentShareUrl] = useState('');
  const [currentShareVideo, setCurrentShareVideo] = useState<any>(null);
  const [noteRecordsDialogOpen, setNoteRecordsDialogOpen] = useState(false);
  const [currentEditingKey, setCurrentEditingKey] = useState<number | null>(null);
  const [currentEditingContent, setCurrentEditingContent] = useState('');
  const [currentFirestoreId, setCurrentFirestoreId] = useState<string>('');

  const { user: authUser, logout } = useAuth();
  const { guestId } = useGuestId();
  // 🔧 修復：統一使用 authUser?.uid 而不是 email，與 ManagePages 保持一致
  const userId = authUser?.uid || guestId || 'guest';

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 1. 新增 tabNumberKeys 狀態
  const TAB_KEYS = ['YouTube', 'Video', 'Google'];
  const [currentTab, setCurrentTab] = useState('YouTube');
  const [tabNumberKeys, setTabNumberKeys] = useState(() => {
    const obj: Record<string, any> = {};
    TAB_KEYS.forEach(tab => { obj[tab] = getInitialNumberKeyState(); });
    return obj;
  });

  // 2. 添加其他必要的狀態變數
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [mButtonState, setMButtonState] = useState<'M' | 'PB' | 'MS' | 'AB' | 'MV'>('M');
  const [keySetIndex, setKeySetIndex] = useState<number>(0);
  const [ppPlayDuration, setPPPlayDuration] = useState<number>(Number(localStorage.getItem('ppPlayDuration')) || 5);
  const [maxNumberKeys, setMaxNumberKeys] = useState<number>(18);
  const [isSubtitleBoardActive, setIsSubtitleBoardActive] = useState<boolean>(false);

  // 3. 準備 Hook 配置
  const numberKeyConfig = {
    maxNumberKeys,
    keySetIndex,
    ppPlayDuration,
    playbackRate,
    showTimecode: true,
    isUserLoggedIn: !!authUser,
    currentTab: tabValue,
    tabNumberKeys: {
      0: tabNumberKeys['YouTube'] || {},
      1: tabNumberKeys['Video'] || {},
      2: tabNumberKeys['Google'] || {},
      3: numberKeys // MULTIVIEW Tab
    },
    currentTime,
    duration,
    isPlaying: playing,
    videoUrl,
    user: authUser
  };

  // 3. 移除循環依賴的 useEffect，直接使用 tabNumberKeys[currentTab]
  // 不再需要同步 numberKeys 和 tabNumberKeys

  // 🔧 修復：載入重點數的邏輯 - 移到 useEffect 之前
  const loadVideoData = useCallback(async (url: string) => {
    console.log('🚀 loadVideoData 函數被調用，URL:', url);
    if (!url) {
      console.log('⚠️ loadVideoData: URL 為空');
      return;
    }
    
    try {
      console.log('🔍 開始載入重點數，URL:', url);
      console.log('🔍 當前 authUser:', authUser?.email);
      
      // 提取 YouTube ID
      const cleanVideoId = extractYouTubeId(url);
      if (!cleanVideoId) {
        console.log('⚠️ 無法提取 YouTube ID');
        return;
      }
      
      console.log('🔍 提取的 YouTube ID:', cleanVideoId);
      
      // 查詢 sharedVbids 集合 - 使用 videoId 而不是 path
      const { collection, query, where, orderBy, limit, getDocs } = await import('firebase/firestore');
      const { getDb, waitForFirebase } = await import('../services/firebase');
      
      // 等待 Firebase 初始化
      await waitForFirebase();
      const db = getDb();
      if (!db) {
        console.warn('⚠️ Firestore 未初始化，跳過查詢');
        return;
      }
      
      // 先嘗試用 videoId 查詢 sharedVbids
      const userQuery = query(
        collection(db, "sharedVbids"),
        where("videoId", "==", cleanVideoId),
        where("userId", "==", authUser?.uid || "")
      );
      
      let querySnapshot = await getDocs(userQuery);
      
      // 如果當前用戶沒有文檔，則查詢所有文檔
      if (querySnapshot.empty) {
        const allQuery = query(
          collection(db, "sharedVbids"),
          where("videoId", "==", cleanVideoId)
        );
        querySnapshot = await getDocs(allQuery);
      }
      
      // 同時查詢 userNotes 和 sharedVbids
      console.log('🔍 同時查詢 userNotes 和 sharedVbids...');
      
      // 🔧 跳過 userNotes 查詢，資料已遷移到 sharedVbids
      console.log('🔍 跳過 userNotes 查詢，資料已遷移到 sharedVbids');
      const notesSnapshot: any = { forEach: () => {}, docs: [], empty: true }; // 空的操作
      console.log(`🔍 跳過 userNotes 查詢，資料已遷移到 sharedVbids`);
      
      let finalNumberKeys: Record<string, NumberKeyState> = {};
      
      // 1. 先處理 sharedVbids 數據（如果存在）
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const data = doc.data();
        
        console.log('✅ 找到 sharedVbids 記錄:', data);
        
        if (data.vbids && data.vbids.length > 0) {
          console.log('✅ 從 sharedVbids 載入重點數:', data.vbids.length);
          const sharedVbidsNumberKeys = vbidsToNumberKeys(data.vbids);
          finalNumberKeys = { ...finalNumberKeys, ...sharedVbidsNumberKeys };
          console.log('🔧 從 sharedVbids 轉換為 numberKeys:', sharedVbidsNumberKeys);
        }
      }
      
      // 2. 處理 userNotes 數據（如果存在）
      if (!notesSnapshot.empty) {
        console.log(`✅ 從 userNotes 找到 ${notesSnapshot.docs.length} 個重點`);
        
        notesSnapshot.docs.forEach((noteDoc: any) => {
          const noteData = noteDoc.data();
          const keyNo = noteData.keyNumber;
          
          // 🔧 修復：確保 isActive 狀態正確設置
          // 如果有時間碼或有 Note 內容，則設為活躍狀態
          const hasTimecode = noteData.timecode && noteData.timecode !== '00:00:00 00';
          const hasNote = noteData.note && noteData.note.trim() !== '';
          const isActive = hasTimecode || hasNote;
          
          const userNoteKey = {
            keyNo: keyNo,
            isActive: isActive,
            isABPoint: false,
            isPickUpState: false,
            timestamp: (() => {
              const { sanitizeTimestamp } = require('../utils/timestampValidator');
              return sanitizeTimestamp(noteData.timestamp);
            })(),
            videoMode: null,
            frame: null,
            timecode: noteData.timecode || '00:00:00 00',
            note: noteData.note || '',
            screenshot: noteData.screenshot || ''
          };
          
          // 🔧 修復：如果 userNotes 中已有該 keyNo，保留 userNotes 的數據
          if (finalNumberKeys[keyNo.toString()]) {
            console.log(`🔧 保留 userNotes 數字鍵 ${keyNo}，跳過 sharedVbids 覆蓋`);
            // 保留 userNotes 的數據，但確保 isActive 狀態正確
            finalNumberKeys[keyNo.toString()] = {
              ...finalNumberKeys[keyNo.toString()],
              isActive: isActive,
              note: noteData.note || '',
              timecode: noteData.timecode || finalNumberKeys[keyNo.toString()].timecode
            };
          } else {
            // 如果 sharedVbids 中沒有該 keyNo，直接添加 userNotes 的數據
            finalNumberKeys[keyNo.toString()] = userNoteKey;
          }
          
          console.log(`📝 處理 userNote keyNo ${keyNo}: timecode="${noteData.timecode}", note="${noteData.note}", isActive=${isActive}`);
        });
      }
      
      // 3. 設置最終的 numberKeys
      if (Object.keys(finalNumberKeys).length > 0) {
        setNumberKeys(finalNumberKeys);
        console.log('✅ 成功載入合併的重點數:', finalNumberKeys);
      } else {
        console.log('⚠️ 沒有找到任何重點數，保持現有狀態');
        // 🔧 修復：不要重置現有狀態，避免丟失用戶已創建的重點數
        // setNumberKeys(getInitialNumberKeyState());
      }
      
      // 🔧 調試：檢查載入的數據是否完整
      console.log('🔍 載入結果統計:', {
        sharedVbids: !querySnapshot.empty ? querySnapshot.docs.length : 0,
        userNotes: !notesSnapshot.empty ? notesSnapshot.docs.length : 0,
        finalNumberKeys: Object.keys(finalNumberKeys).length,
        keys: Object.keys(finalNumberKeys)
      });
      
    } catch (error) {
      console.error('❌ 載入重點數失敗:', error);
      // 🔧 修復：錯誤時也不要重置現有狀態，避免丟失用戶已創建的重點數
      // setNumberKeys(getInitialNumberKeyState());
    }
  }, [authUser]);

  // 4. 處理從 OpenPage 或 NotesPage 傳遞的狀態
  useEffect(() => {
    if (openPageState) {
      console.log('🔄 收到導航狀態:', openPageState);

      // 🔧 新增：處理從雲端上傳來的檔案
      if (openPageState.googleDriveUrl && openPageState.fromCloudUpload) {
        console.log('☁️ 從雲端上傳接收到檔案:', openPageState.googleDriveTitle);
        console.log('🔍 雲端檔案詳情:', {
          url: openPageState.googleDriveUrl,
          title: openPageState.googleDriveTitle,
          path: openPageState.googleDrivePath,
          cloudFile: openPageState.cloudFile
        });
        
        // 設定影片 URL（添加 ?alt=media 參數以確保可以訪問）
        const videoUrl = openPageState.googleDriveUrl.includes('?alt=media') 
          ? openPageState.googleDriveUrl 
          : `${openPageState.googleDriveUrl}?alt=media`;
        setVideoUrl(videoUrl);
        
        // 設定為 VIDEO 模式（使用 CheckPlay 播放器）
        setCurrentMode('VIDEO');
        
        // 自動切換到 VIDEO Tab (index 2)
        setTabValue(2);
        
        console.log('✅ 已設定雲端檔案，使用 CheckPlay 播放器播放');
        return; // 提前返回，不執行後面的邏輯
      }

      if (openPageState.source === 'youtube' && openPageState.videoUrl) {
        console.log('🎬 設置 YouTube URL:', openPageState.videoUrl);
        setVideoUrl(openPageState.videoUrl);
        setCurrentMode('YouTube');
        setTabValue(3); // 切換到 YouTube Tab
        
        // 🔧 修復：從 OpenPage state 中獲取完整的 firestoreId
        if (openPageState.firestoreId) {
          console.log('🔧 從 OpenPage 設定完整的 firestoreId:', openPageState.firestoreId);
          setCurrentFirestoreId(openPageState.firestoreId);
        }
        
        // 🔧 處理從 NotesPage 傳遞的重點數據
        if (openPageState.vbids && Array.isArray(openPageState.vbids)) {
          console.log('🎯 從 NotesPage 收到重點數據:', openPageState.vbids);
          
          // 將 vbids 轉換為 numberKeys 格式
          const newNumberKeys: Record<string, NumberKeyState> = {};
          openPageState.vbids.forEach((vbid: any) => {
            newNumberKeys[vbid.keyNo.toString()] = {
              keyNo: vbid.keyNo,
              isActive: vbid.isActive,
              isABPoint: vbid.isABPoint,
              isPickUpState: vbid.isPickUpState,
              timestamp: vbid.timestamp,
              videoMode: vbid.videoMode,
              frame: vbid.frame,
              timecode: vbid.timecode,
              note: vbid.note,
              screenshot: vbid.screenshot
            };
          });
          
          console.log('🎯 轉換後的 numberKeys:', newNumberKeys);
          setNumberKeys(newNumberKeys);
          
          // 🔧 處理跳轉到特定重點
          if (openPageState.jumpToCheckpoint) {
            console.log('🎯 跳轉到重點:', openPageState.jumpToCheckpoint);
            
            // 找到對應的重點數據
            const targetVbid = openPageState.vbids.find((vbid: any) => vbid.keyNo === openPageState.jumpToCheckpoint);
            
            if (targetVbid && targetVbid.timestamp) {
              console.log('🎯 找到目標重點，時間戳記:', targetVbid.timestamp);
              
              // 延遲執行跳轉，確保影片已經載入
              setTimeout(() => {
                console.log('🎯 執行跳轉到時間戳記:', targetVbid.timestamp);
                
                // 使用 YouTube Player API 跳轉
                const player = document.querySelector('iframe[src*="youtube.com"]') as HTMLIFrameElement;
                if (player && player.contentWindow) {
                  // 發送跳轉指令到 YouTube 播放器
                  player.contentWindow.postMessage(JSON.stringify({
                    event: 'command',
                    func: 'seekTo',
                    args: [targetVbid.timestamp, true]
                  }), '*');
                  
                  console.log('✅ 已發送跳轉指令到 YouTube 播放器');
                } else {
                  console.warn('⚠️ 找不到 YouTube 播放器元素');
                }
              }, 2000); // 2秒延遲，確保影片載入完成
            } else {
              console.warn('⚠️ 找不到目標重點或時間戳記:', openPageState.jumpToCheckpoint);
            }
          }
        } else {
          // 🔧 修復：直接調用 loadVideoData 載入重點數
          console.log('🔍 從 OpenPage 進入，直接載入重點數...');
          console.log('🔍 調用 loadVideoData 函數，URL:', openPageState.videoUrl);
          loadVideoData(openPageState.videoUrl).catch(error => {
            console.error('❌ loadVideoData 調用失敗:', error);
          });
          console.log('🔍 loadVideoData 函數調用完成');
        }
        
        navigate(location.pathname, { replace: true, state: {} });
      } else if (openPageState.source === 'local' && openPageState.localFile) {
        console.log('📁 處理本機檔案:', openPageState.localFile.name);
        setTabValue(2); // 切換到 Video Tab
        navigate(location.pathname, { replace: true, state: {} });
        setTimeout(() => {
          console.log('📁 已切換到 Video Tab，用戶可以手動選擇檔案');
        }, 100);
      }
    }
  }, [openPageState, navigate, location.pathname, loadVideoData]);


  // 5. 數字鍵有變動時，直接更新對應 Tab 狀態（移除這個 useEffect）
  // useEffect(() => {
  //   setTabNumberKeys(prev => ({ ...prev, [currentTab]: numberKeys }));
  // }, [numberKeys]);

  // 5. Tab 切換 UI（假設有一個 TabBar）
  // <TabBar value={currentTab} onChange={setCurrentTab} tabs={TAB_KEYS} />
  // ... existing code ...
  // 6. 數字鍵面板只渲染當前 Tab 的 numberKeys
  // <NumberKeyPanel numberKeys={tabNumberKeys[currentTab]} ... />
  // ... existing code ...

  // 1. 先定義所有 handler
  const handleMyFavorite = useCallback(() => {
    console.log('MainPage handleMyFavorite 被呼叫');
    console.log('開始處理 My Favorite 請求');
    
    try {
      // 保存當前狀態
      const stateToSave = {
        currentMode: 'YouTube', // 強制設置為 YouTube
        videoUrl,
        numberKeys,
        abPoints,
        currentVbids,
        currentTime,
        duration,
        playing: false, // 確保暫停播放
        volume,
        muted,
        playbackRate,
        played,
        loaded,
        seeking,
        showControls,
        showNumberKeyPanel,
        showABPointPanel,
        showVBIDPanel,
        showImageEditor,
        imageUrl,
        selectedFontSize,
        lineWidthMap,
        history,
        historyIndex,
        tabValue
      };

      console.log('準備保存狀態:', stateToSave);

      // 保存到 localStorage 和 sessionStorage
      localStorage.setItem('mainPageStateBackup', JSON.stringify(stateToSave));
      sessionStorage.setItem('mainPageState', JSON.stringify(stateToSave));

      // 設置保留狀態的標記
      localStorage.setItem('preserveMainPageState', 'true');
      sessionStorage.setItem('preserveMainPageState', 'true');
      localStorage.setItem('returnFromFavorites', 'true');
      sessionStorage.setItem('returnFromFavorites', 'true');

      // 設置全局保護標誌
      (window as any).__mainPagePreserveState = true;
      (window as any).__statePreservationTimestamp = Date.now();

      console.log('狀態已保存，準備導航到我的重點管理頁面');

      // 導航到我的重點管理頁面
      navigate('/favorite', {
        state: {
          ...stateToSave,
          currentSection: 'personalHistory', // 預設進入「瀏覽記錄」分頁
          returnFromFavorites: true,
          timestamp: Date.now()
        }
      });
    } catch (error) {
      console.error('處理 My Favorite 時發生錯誤:', error);
    }
  }, [
    videoUrl,
    numberKeys,
    abPoints,
    currentVbids,
    currentTime,
    duration,
    playing,
    volume,
    muted,
    playbackRate,
    played,
    loaded,
    seeking,
    showControls,
    showNumberKeyPanel,
    showABPointPanel,
    showVBIDPanel,
    showImageEditor,
    imageUrl,
    selectedFontSize,
    lineWidthMap,
    history,
    historyIndex,
    tabValue,
    navigate
  ]);

  const handleGroupVideos = () => {
    // TODO: 實作群組影片功能
    console.log('handleGroupVideos clicked');
  };

  const handleCommunityVideos = () => {
    // TODO: 實作社群影片功能
    console.log('handleCommunityVideos clicked');
  };

  const handleSettings = useCallback(() => {
    console.log('🔧 MainPage (pages) handleSettings 被調用');
    // 🔧 設定功能由 MainPageComponent 內部處理
    // 這裡暫時發送一個自定義事件通知組件開啟設定
    const event = new CustomEvent('openSettings', {
      detail: { source: 'bottomBar' }
    });
    window.dispatchEvent(event);
    console.log('📢 已發送 openSettings 事件');
  }, []);

  const handleNoteToggle = () => {
    setIsSubtitleBoardActive(prev => !prev);
    console.log('handleNoteToggle clicked, isSubtitleBoardActive:', !isSubtitleBoardActive);
  };

  const handleImageManager = () => {
    // TODO: 實作圖片管理功能
    console.log('handleImageManager clicked');
  };

  // 🔧 新增：NoteRecordsDialog 相關函數
  const handleEditNote = useCallback((keyNumber: number, note: string) => {
    console.log(`📝 編輯數字鍵 ${keyNumber} 的 Note:`, note);
    
    // 更新本地狀態
    setNumberKeys(prev => ({
      ...prev,
      [keyNumber]: {
        ...prev[keyNumber],
        note: note,
        hasNote: note.trim() !== ''
      }
    }));
    
    // 更新編輯狀態
    setCurrentEditingKey(keyNumber);
    setCurrentEditingContent(note);
    
    // 保存到 Firestore
    if (authUser && mainId) {
      const userDocRef = doc(db, 'users', authUser.uid);
      const vbidsDocRef = doc(userDocRef, 'vbids', mainId);
      
      updateDoc(vbidsDocRef, {
        [`numberKeys.${keyNumber}.note`]: note,
        [`numberKeys.${keyNumber}.hasNote`]: note.trim() !== ''
      }).then(() => {
        console.log(`✅ 數字鍵 ${keyNumber} 的 Note 已保存到 Firestore`);
      }).catch((error) => {
        console.error(`❌ 保存數字鍵 ${keyNumber} 的 Note 失敗:`, error);
      });
    }
  }, [authUser, mainId]);

  const handleDeleteNote = useCallback((keyNumber: number) => {
    console.log(`🗑️ 刪除數字鍵 ${keyNumber} 的 Note 和時間碼`);
    
    // 更新本地狀態 - 同時清除 Note 和時間碼
    setNumberKeys(prev => ({
      ...prev,
      [keyNumber]: {
        ...prev[keyNumber],
        note: '',
        timecode: null,
        timestamp: null,
        isActive: false,
        hasNote: false
      }
    }));
    
    // 清除編輯狀態
    setCurrentEditingKey(null);
    setCurrentEditingContent('');
    
    // 從 Firestore 中移除 Note 和時間碼
    if (authUser && mainId) {
      const userDocRef = doc(db, 'users', authUser.uid);
      const vbidsDocRef = doc(userDocRef, 'vbids', mainId);
      
      updateDoc(vbidsDocRef, {
        [`numberKeys.${keyNumber}.note`]: '',
        [`numberKeys.${keyNumber}.timecode`]: null,
        [`numberKeys.${keyNumber}.timestamp`]: null,
        [`numberKeys.${keyNumber}.isActive`]: false,
        [`numberKeys.${keyNumber}.hasNote`]: false
      }).then(() => {
        console.log(`✅ 數字鍵 ${keyNumber} 的 Note 和時間碼已從 Firestore 移除`);
      }).catch((error) => {
        console.error(`❌ 移除數字鍵 ${keyNumber} 的 Note 和時間碼失敗:`, error);
      });
    }
  }, [authUser, mainId]);

  const handleClearTimecode = useCallback((keyNumber: number) => {
    console.log(`🗑️ 清除數字鍵 ${keyNumber} 的時間碼`);
    
    // 清除該數字鍵的時間碼
    setNumberKeys(prev => ({
      ...prev,
      [keyNumber]: {
        ...prev[keyNumber],
        timecode: null,
        timestamp: null,
        isActive: false,
        hasNote: false
      }
    }));
    
    // 同時從 Firestore 中移除該記錄
    if (authUser && mainId) {
      const userDocRef = doc(db, 'users', authUser.uid);
      const vbidsDocRef = doc(userDocRef, 'vbids', mainId);
      
      updateDoc(vbidsDocRef, {
        [`numberKeys.${keyNumber}`]: {
          timecode: null,
          timestamp: null,
          isActive: false,
          hasNote: false
        }
      }).then(() => {
        console.log(`✅ 數字鍵 ${keyNumber} 的時間碼已從 Firestore 清除`);
      }).catch((error) => {
        console.error(`❌ 清除數字鍵 ${keyNumber} 時間碼失敗:`, error);
      });
    }
  }, [authUser, mainId]);



  const handleTimecodeClick = useCallback((keyNumber: number, timestamp: number) => {
    console.log(`🎯 點擊時間碼，跳轉到數字鍵 ${keyNumber} 的時間點:`, timestamp);
    // 這裡可以實現時間碼跳轉邏輯
  }, []);

  const handleCleanupData = useCallback(() => {
    console.log('🧹 清理數據');
    // 這裡可以實現數據清理邏輯
  }, []);

  const handleSaveReorderedData = useCallback((reorderedRecords: any[]) => {
    console.log('💾 保存重新排序後的數據:', reorderedRecords);
    // 這裡可以實現保存重新排序數據的邏輯
  }, []);

  // 🔧 修復：使用與 MainLayout 一致的 menuItems，避免選單內容變化
  const mainPageMenuItems = [
    {
      key: 'settings',
      label: '設定',
      icon: <SettingsIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        console.log('開啟設定視窗');
        // TODO: 實作設定功能
      }
    },
    {
      key: 'communityPublic',
      label: '社群公開',
      icon: <PublicIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        navigate('/manage?section=communityPublic');
      }
    },
    {
      key: 'teamPoint',
      label: '群組影片',
      icon: <GroupIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        navigate('/manage?section=teamPoint');
      }
    },
    {
      key: 'expertKnowledge',
      label: '專家筆記',
      icon: <NoteIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        navigate('/manage?section=expertKnowledge');
      }
    },
    {
      key: 'myVideos',
      label: '我的創作',
      icon: <PhotoLibraryIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        navigate('/manage?section=myVideos');
      }
    },
    {
      key: 'personalHistory',
      label: '瀏覽記錄',
      icon: <HistoryIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        navigate('/manage?section=personalHistory');
      }
    },
    {
      key: 'imageManager',
      label: '圖片管理',
      icon: <PhotoLibraryIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        navigate('/manage');
      }
    }
  ];

  console.log('mainPageMenuItems:', mainPageMenuItems);

  // 添加更詳細的 debug
  console.log('=== MAINPAGE MENU ITEMS DEBUG ===');
  console.log('mainPageMenuItems length:', mainPageMenuItems.length);
  console.log('mainPageMenuItems keys:', mainPageMenuItems.map(item => item.key));
  console.log('handleMyFavorite type:', typeof handleMyFavorite);
  console.log('isMobile:', isMobile);
  console.log('=== END MENU ITEMS DEBUG ===');

  // 🔧 移除重複的 useEffect，避免與第一個 useEffect 衝突
  // 第一個 useEffect 已經處理了所有 openPageState 的情況

  // 保存快照的函數 - 優化性能，減少依賴項
  const saveSnapshot = useCallback(() => {
    if (isRestoringSnapshot.current) {
      console.log('🟡 正在恢復快照，跳過保存');
      return;
    }

    // 使用 ref 獲取最新狀態，避免大量依賴項
    const getCurrentState = () => ({
      currentMode,
      videoUrl,
      numberKeys,
      abPoints,
      currentVbids,
      currentTime,
      duration,
      playing,
      volume,
      muted,
      playbackRate,
      played,
      loaded,
      seeking,
      showControls,
      showNumberKeyPanel,
      showABPointPanel,
      showVBIDPanel,
      showImageEditor,
      imageUrl,
      selectedFontSize,
      lineWidthMap,
      history,
      historyIndex,
      tabValue
    });

    try {
      const state = getCurrentState();
      localStorage.setItem(snapshotKey, JSON.stringify(state));
      console.log('🟢 快照已保存');
    } catch (error) {
      console.error('❌ 保存快照失敗:', error);
    }
  }, []); // 移除所有依賴項，使用內部函數獲取最新狀態

  // 檢查是否需要恢復狀態
  useEffect(() => {
    console.log('🟠 檢查是否需要恢復狀態');
    
    const checkAndRestoreState = () => {
      // 檢查 sessionStorage 和 localStorage 中的保留標記
      const preserveSession = sessionStorage.getItem('preserveMainPageState') === 'true';
      const preserveLocal = localStorage.getItem('preserveMainPageState') === 'true';
      const returnFromFavorites = sessionStorage.getItem('returnFromFavorites') === 'true' || 
                                localStorage.getItem('returnFromFavorites') === 'true';
      
      console.log('🟠 狀態保留標記:', {
        preserveSession,
        preserveLocal,
        returnFromFavorites
      });
      
      if (preserveSession || preserveLocal || returnFromFavorites) {
        console.log('🟠 檢測到需要恢復狀態');
        
        // 從 localStorage 或 sessionStorage 讀取保存的狀態
        const savedState = localStorage.getItem('mainPageStateBackup') || 
                          sessionStorage.getItem('mainPageState');
        
        if (savedState) {
          try {
            const state = JSON.parse(savedState);
            console.log('🟠 成功讀取保存的狀態:', state);
            
            // 確保 currentMode 是 'YouTube'
            state.currentMode = 'YouTube';
            
            // 設置全局保護標誌
            (window as any).__mainPagePreserveState = true;
            (window as any).__statePreservationTimestamp = Date.now();
            
            // 恢復所有狀態
            setCurrentMode(state.currentMode);
            setVideoUrl(state.videoUrl);
            setNumberKeys(state.numberKeys);
            setAbPoints(state.abPoints);
            setCurrentVbids(state.currentVbids);
            setCurrentTime(state.currentTime);
            setDuration(state.duration);
            setPlaying(state.playing);
            setVolume(state.volume);
            setMuted(state.muted);
            setPlaybackRate(state.playbackRate);
            setPlayed(state.played);
            setLoaded(state.loaded);
            setSeeking(state.seeking);
            setShowControls(state.showControls);
            setShowNumberKeyPanel(state.showNumberKeyPanel);
            setShowABPointPanel(state.showABPointPanel);
            setShowVBIDPanel(state.showVBIDPanel);
            setShowImageEditor(state.showImageEditor);
            setImageUrl(state.imageUrl);
            setSelectedFontSize(state.selectedFontSize);
            setLineWidthMap(state.lineWidthMap);
            setHistory(state.history);
            setHistoryIndex(state.historyIndex);
            setTabValue(state.tabValue);
            // 恢復 mButtonState（如果存在）
            if (state.mButtonState) {
              setMButtonState(state.mButtonState);
            }
            
            console.log('🟢 狀態已恢復');
            
            // 延遲清除保留標記
            setTimeout(() => {
              sessionStorage.removeItem('preserveMainPageState');
              localStorage.removeItem('preserveMainPageState');
              sessionStorage.removeItem('returnFromFavorites');
              localStorage.removeItem('returnFromFavorites');
              console.log('🟢 延遲清理保留標記');
            }, 1000);
          } catch (error) {
            console.error('恢復狀態時發生錯誤:', error);
          }
        }
      }
    };
    
    checkAndRestoreState();
  }, []);

  // 移除自動保存快照的 useEffect，避免性能問題
  // 改為在特定操作時手動保存快照
  // useEffect(() => {
  //   const timeoutId = setTimeout(() => {
  //     console.log('🟠 狀態改變，準備保存快照');
  //     saveSnapshot();
  //   }, 1000); // 1秒節流

  //   return () => clearTimeout(timeoutId);
  // }, [saveSnapshot]);

  // 在組件卸載時清除快照
  useEffect(() => {
    const handleUnload = () => {
      console.log('🟠 組件卸載，清除快照');
      localStorage.removeItem(snapshotKey);
    };
    window.addEventListener('beforeunload', handleUnload, { passive: true });
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);

  // 🔧 修復：移除調試用的 useEffect，避免不必要的性能開銷
  // useEffect(() => {
  //   console.log('=====================================================');
  //   console.log('🔍 MAINPAGE MOUNT DEBUG 🔍');
  //   console.log('handleMyFavorite is defined:', !!handleMyFavorite);
  //   console.log('handleMyFavorite type:', typeof handleMyFavorite);
  //   console.log('handleMyFavorite toString:', handleMyFavorite.toString());
  //   console.log('=====================================================');
  // }, [handleMyFavorite]);

  // useEffect(() => {
  //   if (!handleMyFavorite) {
  //     console.error('handleMyFavorite is not defined on mount');
  //   }
  // }, []);

  // 監控狀態變化 - 暫時註解掉，可能導致無限循環
  /*
  useEffect(() => {
    console.log('=====================================================');
    console.log('🔍 MAINPAGE STATE CHANGE DEBUG 🔍');
    console.log('State changed:', {
      currentMode,
      videoUrl,
      numberKeys,
      abPoints,
      currentVbids,
      currentTime,
      duration,
      playing,
      volume,
      muted,
      playbackRate,
      played,
      loaded,
      seeking,
      showControls,
      showNumberKeyPanel,
      showABPointPanel,
      showVBIDPanel,
      showImageEditor,
      imageUrl,
      selectedFontSize,
      lineWidthMap,
      history,
      historyIndex,
      vbidDialogOpen
    });
    console.log('=====================================================');
  }, [
    currentMode,
    videoUrl,
    numberKeys,
    abPoints,
    currentVbids,
    currentTime,
    duration,
    playing,
    volume,
    muted,
    playbackRate,
    played,
    loaded,
    seeking,
    showControls,
    showNumberKeyPanel,
    showABPointPanel,
    showVBIDPanel,
    showImageEditor,
    imageUrl,
    selectedFontSize,
    lineWidthMap,
    history,
    historyIndex,
    vbidDialogOpen
  ]);
  */

  // 確保所有狀態在組件掛載時都被正確初始化 - 暫時註解掉
  /*
  useEffect(() => {
    console.log('=====================================================');
    console.log('🟢 MAINPAGE INITIALIZATION DEBUG 🔍');
    console.log('Initializing state with values:', {
      currentMode,
      videoUrl,
      numberKeys,
      abPoints,
      currentVbids,
      currentTime,
      duration,
      playing,
      volume,
      muted,
      playbackRate,
      played,
      loaded,
      seeking,
      showControls,
      showNumberKeyPanel,
      showABPointPanel,
      showVBIDPanel,
      showImageEditor,
      imageUrl,
      selectedFontSize,
      lineWidthMap,
      history,
      historyIndex,
      vbidDialogOpen
    });
    console.log('=====================================================');
  }, []);
  */

  const handleVbidDialogClose = () => {
    setVbidDialogOpen(false);
  };

  const handleImportVbids = (newVbids: any[]) => {
    setCurrentVbids(newVbids);
    setVbidDialogOpen(false);
  };

  const handleBookmarkClick = () => {
    // 播放按鈕點擊音效
    if (typeof playButtonSound === 'function') {
      playButtonSound('click');
    }
    
    // 按下「我的重點」時暫停影片
    setPlaying(false);
    console.log('Bookmark button clicked');
    
    // 確保 currentMode 是 'YouTube'
    setCurrentMode('YouTube');
    console.log('Current mode set to:', 'YouTube');
    
    // 使用新的轉換函數來獲取所有數字鍵的 VBID，並補齊所有欄位（明確給預設值）
    console.log('🔍 handleBookmarkClick - tabNumberKeys[currentTab]:', tabNumberKeys[currentTab]);
    console.log('🔍 handleBookmarkClick - currentTab:', currentTab);
    const currentVBIDs = convertNumberKeysToVBIDs(tabNumberKeys[currentTab], 'YouTube').map((vbid, idx) => ({
      keyNo: idx + 1,
      timecode: vbid.timecode ?? '',
      note: vbid.note ?? '',
      screenshot: vbid.screenshot ?? '',
      flag: vbid.flag ?? 'on',
      mode: 'YouTube',
      path: videoUrl,
      userId: userId,
      privacy: 'Private' as const,
      kkeyNO: idx + 1,
      ttimeCodeStart: vbid.timecode ?? '',
      ttimeCodeEnd: vbid.timecode ?? '',
      addedDate: new Date().toISOString(),
      mediaMode: vbid.mediaMode ?? 'YouTube',
      playMode: vbid.playMode ?? 'M',
      frame: vbid.frame ?? 0
    }));
    const uniqueCurrentVBIDs = filterDuplicateVBIDs(currentVBIDs);

    console.log('handleBookmarkClick - currentVBIDs:', currentVBIDs);
    console.log('handleBookmarkClick - uniqueCurrentVBIDs:', uniqueCurrentVBIDs);
    console.log('handleBookmarkClick - numberKeys:', tabNumberKeys[currentTab]);

    // 確保初始化為空陣列
    const safeVBIDs = uniqueCurrentVBIDs || [];
    console.log('handleBookmarkClick - safeVBIDs:', safeVBIDs);

    // 更新當前顯示的 VBID，確保是空陣列而不是 undefined
    if (safeVBIDs.length > 0) {
      console.log('safeVBIDs[0] keys:', Object.keys(safeVBIDs[0]));
      console.log('safeVBIDs[0] full:', safeVBIDs[0]);
    }
    setCurrentVbids(safeVBIDs);
    setVbids(safeVBIDs);
    
    // 打開對話框並更新按鈕狀態
    setVbidDialogOpen(true);
    setIsBookmarkEnabled(safeVBIDs.length > 0);
    
    console.log('handleBookmarkClick - final safeVBIDs length:', safeVBIDs.length);
    
    // 保存狀態到 localStorage
    const stateToSave = {
      currentMode: 'YouTube',
      videoUrl,
      numberKeys: tabNumberKeys[currentTab],
      abPoints,
      currentVbids: safeVBIDs,
      currentTime,
      duration,
      playing: false,
      volume,
      muted,
      playbackRate,
      played,
      loaded,
      seeking,
      showControls,
      showNumberKeyPanel,
      showABPointPanel,
      showVBIDPanel,
      showImageEditor,
      imageUrl,
      selectedFontSize,
      lineWidthMap,
      history,
      historyIndex,
      tabValue
    };
    
    localStorage.setItem('mainPageStateBackup', JSON.stringify(stateToSave));
    sessionStorage.setItem('mainPageState', JSON.stringify(stateToSave));
  };

  const handleSaveVBIDs = () => {
    setVbidDialogOpen(false);
  };

  const handleSetTabVbids = useCallback((vbids: VBID[]) => {
    setTabVbids(prev => ({ ...prev, [tabValue]: vbids }));
  }, [tabValue]);

  // 🔧 修復：移除大型狀態調試 useEffect，避免嚴重的性能問題
  // useEffect(() => {
  //   console.log('=====================================================');
  //   console.log('🔍 MAINPAGE STATE DEBUG 🔍');
  //   console.log('Current state:', {
  //     currentMode,
  //     videoUrl,
  //     numberKeys,
  //     abPoints,
  //     currentVbids,
  //     currentTime,
  //     duration,
  //     playing,
  //     volume,
  //     muted,
  //     playbackRate,
  //     played,
  //     loaded,
  //     seeking,
  //     showControls,
  //     showNumberKeyPanel,
  //     showABPointPanel,
  //     showVBIDPanel,
  //     showImageEditor,
  //     imageUrl,
  //     selectedFontSize,
  //     lineWidthMap,
  //     history,
  //     historyIndex,
  //     vbidDialogOpen
  //   });
  //   console.log('=====================================================');
  // }, [
  //   currentMode,
  //   videoUrl,
  //   numberKeys,
  //   abPoints,
  //   currentVbids,
  //   currentTime,
  //   duration,
  //   playing,
  //   volume,
  //   muted,
  //   playbackRate,
  //   played,
  //   loaded,
  //   seeking,
  //   showControls,
  //   showNumberKeyPanel,
  //   showABPointPanel,
  //   showVBIDPanel,
  //   showImageEditor,
  //   imageUrl,
  //   selectedFontSize,
  //   lineWidthMap,
  //   history,
  //   historyIndex,
  //   vbidDialogOpen
  // ]);

  // 1. GuestId 產生與管理
  // function getOrCreateGuestId() {
  //   let guestId = localStorage.getItem('guestId');
  //   if (!guestId) {
  //     guestId = 'guest_' + Math.random().toString(36).substring(2) + Date.now();
  //     localStorage.setItem('guestId', guestId);
  //   }
  //   return guestId;
  // }
  //   const guestId = getOrCreateGuestId();

  // 2. saveAllToDatabase 雛型
  async function saveAllToDatabase() {
    // 這裡應呼叫 API 寫入 DB，以下為範例結構
    const checkpoints = convertNumberKeysToVBIDs(numberKeys, currentMode);
    if (!checkpoints.length) return;
    try {
      // TODO: 請替換為實際 API 路徑
      await fetch('/api/v1/guest/checkpoints/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guestId: userId, checkpoints })
      });
      // 寫入成功可清空本地暫存（如有需要）
      // setNumberKeys(getInitialNumberKeyState());
    } catch (err) {
      console.error('儲存到資料庫失敗', err);
    }
  }


  // 🔧 修復：移除渲染期間的 console.log，避免性能問題
  // console.log('MainPage render: typeof handleMyFavorite', typeof handleMyFavorite, handleMyFavorite);

  // console.log('MainPage 傳遞給 VbidDialog 的 onMyFavorite:', typeof handleMyFavorite, handleMyFavorite);

  // 🔧 修復：移除調試用的 useEffect，避免不必要的性能開銷
  // useEffect(() => {
  //   console.log('VbidDialog props onMyFavorite:', typeof handleMyFavorite, handleMyFavorite);
  // }, [handleMyFavorite]);

  // 🔧 修復：移除渲染期間的 console.log，避免性能問題
  // console.log('MainPage 準備傳遞給 VbidDialog 的 props:', {
  //   onMyFavorite: handleMyFavorite,
  //   typeofOnMyFavorite: typeof handleMyFavorite,
  //   vbidDialogOpen
  // });

  // 新增：組合 video 物件並呼叫共用 handleShareVideo
  const handleShareVideo = (video: {
    id?: string;
    videoId?: string;
    path?: string;
    title?: string;
    checkpoints?: any[];
    userId?: string;
    mode?: string;
    // ...其他欄位
  }) => {
    // 這裡可複用 ManagePages 的 handleShareVideo 實作
    setShareMessage('');
    setShareStep('input');
    setCurrentShareId('');
    setCurrentShareUrl(video.id || video.videoId || video.path || videoUrl); // 以 id 或 videoId 或 path 作為識別
    setShareDialogOpen(true);
    setCurrentShareVideo(video); // 新增：記錄目前分享的 video 物件
  };

  // 組合新版 vbids 給 ShareDialog
  const getVbidsForShare = () => {
    // 用 currentVbids 狀態組合分享資料
    const vbids: Record<string, {
      timestamp: number;
      timecode: string;
      isActive: boolean;
    }> = {};
    (currentVbids || []).forEach((vbid, idx) => {
      vbids[String(idx)] = {
        timestamp: vbid.timestamp || 0,
        timecode: vbid.timecode,
        isActive: vbid.flag === 'on'
      };
    });
    return vbids;
  };

  // 🔧 修復：移除調試用的 useEffect，避免不必要的性能開銷
  // useEffect(() => {
  //   console.log('[ShareDialog] 已掛載新版 ShareDialog');
  // }, []);

  const testVbids = [
    { keyNo: 1, timecode: '1_00000012', note: '', screenshot: '', flag: 'on', extra: 'test' },
    { keyNo: 2, timecode: '2_00000115', note: '', screenshot: '', flag: 'off', extra: 'test2' }
  ];
  const vbidsArray = testVbids; // 直接用這個測試

  // 🔧 修復：移除渲染期間的 console.log，避免性能問題
  // console.log('這是 pages/MainPage.tsx');

  // 移除重複的 useEffect，統一使用 mainId 處理
  // useEffect(() => {
  //   console.log('【useParams 取得的 shareId】', shareId);
  //   console.log('🔍 shareId 類型:', typeof shareId);
  //   if (!shareId) {
  //     console.log('❌ shareId 為空，跳過載入');
  //     return;
  //   }
  //   async function fetchShareData() {
  //     try {
  //       console.log('🔍 開始載入分享資料，shareId:', shareId);
  //       // 使用 getSharedVbidsById 函數，它會依序查詢 sharedLinks -> sharedVbids -> videos
  //       const { getSharedVbidsById } = await import('../services/firestoreApi');
  //       console.log('✅ 成功導入 getSharedVbidsById 函數');
  //       const data = await getSharedVbidsById(shareId as string);
  //       console.log('🔍 getSharedVbidsById 返回結果:', data ? '找到資料' : '未找到資料');
  //       
  //       if (data) {
  //         console.log('【還原 Firestore 資料】', data);
  //         setVbids(data.vbids || []);
  //         setCurrentVbids(data.vbids || []);
  //         
  //         // 處理影片 URL：優先使用 path，如果沒有則從 videoId 構建
  //         let videoUrl = data.path || '';
  //         if (!videoUrl && data.videoId) {
  //           videoUrl = `https://www.youtube.com/watch?v=${data.videoId}`;
  //           console.log('【從 videoId 構建 URL】', videoUrl);
  //         }
  //         setVideoUrl(videoUrl);
  //         
  //         setCurrentMode(data.mode || '');
  //         if ((data.vbids || []).length > 0) {
  //           setVbidDialogOpen(true);
  //           console.log('【自動打開 VbidDialog】', data.vbids);
  //         }
  //       } else {
  //         console.log('【Firestore 無此文件】', shareId);
  //       }
  //     } catch (error) {
  //       console.error('【載入分享資料失敗】', error);
  //       console.error('💥 錯誤詳情:', {
  //         message: error instanceof Error ? error.message : 'Unknown error',
  //         stack: error instanceof Error ? error.stack : 'No stack trace',
  //         shareId: shareId
  //       });
  //     }
  //   }
  //   fetchShareData();
  // }, [shareId]);

  // 統一資料載入邏輯：使用 mainId 載入影片資訊
  useEffect(() => {
    console.log('【統一資料載入】mainId 變化:', mainId);
    console.log('🔍 mainId 類型:', typeof mainId);
    console.log('🔍 location.pathname:', location.pathname);
    console.log('🔍 shareId:', shareId);
    console.log('🔍 queryId:', queryId);
    console.log('🔍 shortCode:', shortCode);
    
    if (!mainId) {
      console.log('❌ mainId 為空，跳過載入');
      return;
    }
    
    async function fetchShareDataByMainId() {
      try {
        console.log('🔍 開始載入分享資料，mainId:', mainId);
        
        // 🔧 修復：檢查 mainId 是否為有效的 YouTube 影片 ID
        let youtubeVideoId = mainId;
        
        // 🔧 新增：處理標準化的文檔 ID 格式（以 vid_ 開頭）
        if (mainId && mainId.startsWith('vid_')) {
          console.log('🔧 檢測到標準化文檔 ID 格式:', mainId);
          // 格式：vid_{userId}_{videoId}_{version}
          // 例如：vid_uc9C6sAfsPT743cQApZ4MmZVoU72_j25W8wd2s2M_v1
          const parts = mainId.split('_');
          if (parts.length >= 3) {
            // 取第三部分作為 YouTube 影片 ID
            const extractedId = parts[2];
            if (extractedId && /^[a-zA-Z0-9_-]{11}$/.test(extractedId)) {
              youtubeVideoId = extractedId;
              console.log('🔧 從標準化文檔 ID 提取 YouTube 影片 ID:', { original: mainId, extracted: extractedId });
            }
          }
        }
        // 處理舊的複合格式的 ID，如 uc9C6sAfsPT743cQApZ4MmZVoU72_cHTHYzQ8ErU_v1
        else if (mainId && mainId.includes('_')) {
          const parts = mainId.split('_');
          console.log('🔧 處理舊複合 ID，分割結果:', parts);
          
          // 尋找有效的 YouTube 影片 ID（11位字符）
          for (let i = 0; i < parts.length; i++) {
            const part = parts[i];
            if (part && /^[a-zA-Z0-9_-]{11}$/.test(part)) {
              // 驗證這個 ID 是否真的是 YouTube ID（不是用戶 ID）
              // 用戶 ID 通常更長，YouTube ID 正好是 11 位
              youtubeVideoId = part;
              console.log('🔧 從舊複合 ID 提取 YouTube 影片 ID:', { original: mainId, extracted: part, index: i });
              break;
            }
          }
        }
        
        // 檢查是否為有效的 YouTube 影片 ID
        const isValidYouTubeId = youtubeVideoId ? /^[a-zA-Z0-9_-]{11}$/.test(youtubeVideoId) : false;
        if (isValidYouTubeId) {
          console.log('🔧 檢測到有效的 YouTube 影片 ID，直接構建 URL:', youtubeVideoId);
          const videoUrl = `https://www.youtube.com/watch?v=${youtubeVideoId}`;
          console.log('🔧 從 ID 構建 YouTube URL:', videoUrl);
          
          // 🔧 修復：設置 currentFirestoreId，使用 mainId 作為 firestoreId
          if (mainId) {
            setCurrentFirestoreId(mainId);
            console.log('🔧 設置 currentFirestoreId (YouTube ID):', mainId);
          }
          
          setVideoUrl(videoUrl);
          setCurrentMode('YouTube');
          
          // 載入該影片的重點數
          await loadVideoData(videoUrl);
          
          // 🆕 重要：檢查字幕緩存並啟動 AI 按鈕
          console.log('🔍 檢查影片字幕緩存:', youtubeVideoId);
          
          // 動態導入 srtCacheService
          const { hasAnySRTCache } = await import('../services/srtCacheService');
          const hasSRT = await hasAnySRTCache(youtubeVideoId);
          
          if (hasSRT) {
            console.log('✅ 檢測到字幕緩存，立即啟動 AI 按鈕:', youtubeVideoId);
            // 🔧 使用自定義事件通知 MainPageComponent 更新 AI 按鈕狀態
            const event = new CustomEvent('srtCacheDetected', {
              detail: { 
                videoId: youtubeVideoId,
                hasSRT: true
              }
            });
            window.dispatchEvent(event);
          } else {
            console.log('ℹ️ 沒有檢測到字幕緩存:', youtubeVideoId);
          }
          
          return;
        }
        
        // 如果是 shortCode，先轉換為 snapshotId
        let targetId = mainId;
        if (shortCode) {
          console.log('【ShortCode 處理】檢測到 shortCode:', shortCode);
          const snapshotId = await getSnapshotIdByShortCode(shortCode);
          if (snapshotId) {
            targetId = snapshotId;
            console.log('【ShortCode 轉換】shortCode -> snapshotId:', snapshotId);
          } else {
            console.log('【ShortCode 錯誤】找不到對應的 snapshotId');
            return;
          }
        }
        
        // 使用 getSharedVbidsById 函數，它會依序查詢 sharedLinks -> sharedVbids -> videos
        const { getSharedVbidsById } = await import('../services/firestoreApi');
        console.log('✅ 成功導入 getSharedVbidsById 函數');
        const data = await getSharedVbidsById(targetId as string);
        console.log('🔍 getSharedVbidsById 返回結果:', data ? '找到資料' : '未找到資料');
        
        if (data) {
          console.log('【還原 Firestore 資料】', data);
          console.log('🔍 資料詳情:', {
            path: data.path,
            videoId: data.videoId,
            vbids: data.vbids ? data.vbids.length : 0,
            mode: data.mode,
            mediaMode: data.mediaMode
          });
          
          // 🔧 修復：設置 currentFirestoreId，讓 VideoPlayer 能正確更新觀看記錄
          if (targetId) {
            setCurrentFirestoreId(targetId);
            console.log('🔧 設置 currentFirestoreId:', targetId);
          }
          
          // 處理影片 URL：優先使用 path，如果沒有則從 videoId 構建
          let videoUrl = data.path || '';
          if (!videoUrl && data.videoId) {
            videoUrl = `https://www.youtube.com/watch?v=${data.videoId}`;
            console.log('【從 videoId 構建 URL】', videoUrl);
          }
          
          console.log('🎬 設置影片 URL:', videoUrl);
          setVideoUrl(videoUrl);
          
          // 設置模式
          const mode = data.mode || data.mediaMode || 'YouTube';
          
          // 設置影片模式
          if (mode === 'VIDEO' || mode === 'Local' || mode === '本地影片') {
            setVideoMode('Local');
            setTabValue(2); // 切換到本地影片 Tab
          } else if (mode === 'YouTube') {
            setVideoMode('YouTube');
            setTabValue(3); // 切換到 YouTube Tab
          }
          
          // 設置 vbids（如果有重點資料）
          const vbidsArray = data.vbids || [];
          if (vbidsArray.length > 0) {
            console.log('🔍 設置 vbids:', vbidsArray);
            setVbids(vbidsArray);
            setCurrentVbids(vbidsArray);
            
            // 將 vbids 轉換為 numberKeys
            console.log('🔄 從 Firestore 的 vbids 還原 numberKeys');
            const numberKeys = vbidsToNumberKeys(vbidsArray);
            console.log('📊 Firestore 還原的 numberKeys 中活躍鍵數量:', Object.keys(numberKeys).length);
            setNumberKeys(numberKeys);
            
            // 同時更新 tabNumberKeys
            console.log('🔵 同時更新 tabNumberKeys');
            setTabNumberKeys(prev => ({
              ...prev,
              [currentTab]: numberKeys
            }));
          }
          
          // 確保影片能正常播放，而不是顯示重點報表
          console.log('🎯 分享連結處理完成，準備播放影片');
          console.log('🎭 設置影片模式:', mode);
          setCurrentMode(mode);
          
          // 如果有 vbids，自動打開 VbidDialog
          if ((data.vbids || []).length > 0) {
            console.log('【自動打開 VbidDialog】', data.vbids.length, '個 vbids');
            setVbidDialogOpen(true);
          }
          
          console.log('🔍 準備執行資料載入完成...');
          console.log('✅ 資料載入完成');
          console.log('🔍 檢查路徑:', location.pathname);
          console.log('🔍 是否包含 /share/:', location.pathname.includes('/share/'));
          console.log('🔍 準備執行自動播放檢查...');
          
          // 檢查是否為分享連結，如果是則設定自動播放模式
          if (location.pathname.includes('/share/')) {
            console.log('【檢測到分享連結】設定自動播放模式');
            setMButtonState('PB');
            
            // 設定播放完成後切換回原始模式的計時器
            const playDuration = 5; // 5秒播放時長
            console.log(`【自動播放】${playDuration}秒後切換回原始模式`);
            
            setTimeout(() => {
              console.log(`【自動播放完成】切換回原始模式: M`);
              setMButtonState('M');
            }, playDuration * 1000);
          }
        } else {
          console.log('【Firestore 無此文件】', targetId);
        }
      } catch (error) {
        console.error('【載入影片失敗】', error);
        console.error('💥 錯誤詳情:', {
          message: error instanceof Error ? error.message : 'Unknown error',
          stack: error instanceof Error ? error.stack : 'No stack trace',
          mainId: mainId,
          shortCode: shortCode
        });
      }
    }
    
    fetchShareDataByMainId();
  }, [mainId, shortCode, location.pathname]);

  console.log('【MainPage render】currentVbids:', currentVbids);

  // 處理自動播放模式 - 延遲執行，確保在其他狀態恢復之後
  useEffect(() => {
    const handleAutoPlay = () => {
      const shared = localStorage.getItem('sharedVideoData');
      console.log('【localStorage sharedVideoData】', shared);
      
      if (shared) {
        try {
          const sharedData = JSON.parse(shared);
          console.log('【解析 sharedVideoData】', sharedData);
          
          // 檢查是否為自動播放模式
          if (sharedData.autoPlayMode) {
            console.log('【自動播放模式】設定為重點5秒模式');
            setMButtonState('PB');
            
            // 設定播放完成後切換回原始模式的計時器
            const playDuration = sharedData.ppPlayDuration || 5; // 預設5秒
            console.log(`【自動播放】${playDuration}秒後切換回原始模式`);
            
            setTimeout(() => {
              const originalMode = sharedData.originalMode || 'M';
              console.log(`【自動播放完成】切換回原始模式: ${originalMode}`);
              setMButtonState(originalMode as 'M' | 'PB' | 'MS' | 'AB' | 'MV');
            }, playDuration * 1000);
          }
          
          // 清除 sharedVideoData，避免重複處理
          localStorage.removeItem('sharedVideoData');
        } catch (error) {
          console.error('【解析 sharedVideoData 失敗】', error);
          localStorage.removeItem('sharedVideoData');
        }
      }
    };
    
    // 延遲執行，確保在其他狀態恢復之後
    setTimeout(handleAutoPlay, 100);
  }, []); // 只在組件掛載時執行一次


  useEffect(() => {
    if (videoUrl) {
      console.log('🔍 videoUrl 變化，載入重點數:', videoUrl);
      
      // 🔧 修復：如果剛從 NotesPage 導航過來，不要覆蓋 numberKeys
      if (openPageState && openPageState.vbids && openPageState.vbids.length > 0) {
        console.log('🔧 檢測到從 NotesPage 導航，跳過 loadVideoData 避免覆蓋 numberKeys');
        return;
      }
      
      // 🔧 修復：從 OpenPage 導航過來的影片應該記錄觀看歷史
      // 檢查是否來自 OpenPage（location.state 中有 source 或 videoUrl）
      const isFromOpenPage = openPageState && (openPageState.source || openPageState.videoUrl);
      
      if (!(window as any).__checkplay_user_initiated_play) {
        // 如果來自 OpenPage，設置為 true（用戶主動操作）
        // 否則設置為 false（自動載入）
        (window as any).__checkplay_user_initiated_play = isFromOpenPage ? true : false;
        console.log('🔧 設置用戶主動播放標記:', isFromOpenPage ? '✅ true (來自 OpenPage)' : '❌ false (自動載入)');
      }
      loadVideoData(videoUrl);
    }
  }, [videoUrl]); // 🔧 修復：移除 openPageState 依賴，避免意外觸發

  // useEffect(() => {
  //   console.log('【測試 useEffect 無依賴項】被觸發');
  // }, []);

  // useEffect(() => {
  //   console.log('【測試 useEffect 有依賴項】被觸發，location.pathname:', location.pathname);
  // }, [location.pathname]);

  // 自動播放 5 秒重點模式，然後自動切回模式
  useEffect(() => {
    // 僅在分享連結且影片已載入時觸發
    if (location.pathname.includes('/share/') && videoUrl) {
      console.log('🎬 自動播放模式啟動');
      setMButtonState('PB');
      // 避免重複觸發：加一個 flag
      if (!window.__checkplay_autoplay_flag) {
        window.__checkplay_autoplay_flag = true;
        setTimeout(() => {
          setMButtonState('M');
          window.__checkplay_autoplay_flag = false;
          console.log('🎬 自動播放完成，切換回原始模式');
        }, 5000);
      }
    }
  }, [location.pathname, videoUrl]);

  const lastMessageTime = useRef<number>(0);
  
  const handleMessage = useCallback((e: MessageEvent) => {
    // 快速檢查，避免不必要的處理
    if (e.data?.type !== 'OPEN_MAIN_LOGIN') return;
    
    // 🔧 修復：添加防抖動，避免頻繁處理
    const now = Date.now();
    if (now - lastMessageTime.current < 100) {
      return;
    }
    lastMessageTime.current = now;
    
    setShowAuthDialog(true);
  }, []);

  useEffect(() => {
    window.addEventListener('message', handleMessage, { passive: true });
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  const handleCloseWelcome = () => {
    setShowWelcome(false);
    localStorage.setItem('welcome_shown', '1');
  };

  // 添加處理標題點擊的函數
  const handleTitleClick = () => {
    setShowWelcome(true);
  };

  const handleLogin = () => {
    console.log('登入按鈕被點擊(pages)');
    setShowAuthDialog(true);
  };

  const handleLogout = async () => {
    try {
      await logout();
      // 登出後，直接導向 ManagePages
      navigate('/manage');
    } catch (error) {
      console.error('登出失敗:', error);
    }
  };

  // 添加數字鍵面板相關的處理函數
  const handleMButtonClick = () => {
    setMButtonState(prev => {
      const states: ('M' | 'PB' | 'MS' | 'AB' | 'MV')[] = ['M', 'PB', 'MS', 'AB', 'MV'];
      const currentIndex = states.indexOf(prev);
      const nextIndex = (currentIndex + 1) % states.length;
      return states[nextIndex];
    });
  };

  const handleModeChange = (newMode: 'M' | 'PB' | 'MS' | 'AB' | 'MV') => {
    setMButtonState(newMode);
  };

  const handleCheckClick = () => {
    // 檢查功能實作
    console.log('Check button clicked');
  };

  const handleDeleteButtonClick = () => {
    setIsDeleteMode(!isDeleteMode);
  };

  const handleDeleteButtonPress = () => {
    // 刪除按鈕按下
    console.log('Delete button pressed');
  };

  const handleDeleteButtonRelease = () => {
    // 刪除按鈕釋放
    console.log('Delete button released');
  };

  const handleSwitchClick = () => {
    // 切換功能
    console.log('Switch button clicked');
  };

  const handleBButtonClick = () => {
    // B 按鈕功能
    console.log('B button clicked');
  };

  const handleSpeedChange = (newSpeed: number) => {
    setPlaybackRate(newSpeed);
  };

  // 4. 準備 Hook 事件處理器（將在 handleNumberKeyUp 定義後使用）

  const getSButtonStyle = () => {
    return {
      backgroundColor: mButtonState === 'M' ? '#4CAF50' : '#2196F3',
      color: 'white'
    };
  };

  // 修改 handleNumberKeyUp 函數，直接更新 tabNumberKeys[currentTab]
  const handleNumberKeyUp = async (key: number) => {
    // 自動擴增數字鍵數量
    if (!isDeleteMode && key === maxNumberKeys) {
      setMaxNumberKeys(maxNumberKeys + 6);
    }
    
    // 如果在刪除模式下，直接刪除時間碼記憶
    if (isDeleteMode) {
      // 如果按鈕已激活，直接刪除時間碼記憶
      if (tabNumberKeys[currentTab][key]?.isActive) {
        // 刪除該按鈕的時間碼記憶
        setTabNumberKeys(prev => {
          const newState = { ...prev };
          newState[currentTab] = { ...newState[currentTab] };
          newState[currentTab][key] = {
            keyNo: key,
            isActive: false,
            isABPoint: false,
            isPickUpState: false,
            timestamp: null,
            timecode: '00:00:00 00',
            videoMode: null,
            frame: null,
            note: '',
            screenshot: '',
            flag: 'off',
            playMode: 'M'
          };
          return newState;
        });

        // 播放刪除音效
        const audio = new Audio('/delete-sound.mp3');
        audio.play();

        // 顯示成功訊息
        setSnackbarMessage('已刪除時間碼記憶');
        setSnackbarOpen(true);
        return;
      }
    }
    
    // 重置所有其他按鈕的綠色狀態
    setTabNumberKeys(prev => {
      const newState = { ...prev };
      newState[currentTab] = { ...newState[currentTab] };
      Object.keys(newState[currentTab]).forEach(k => {
        const numKey = Number(k);
        if (numKey !== key && newState[currentTab][numKey].isPickUpState) {
          newState[currentTab][numKey] = {
            ...newState[currentTab][numKey],
            isPickUpState: false
          };
        }
      });
      return newState;
    });

    // 獲取當前時間
    const clickTime = Date.now();
    const lastClick = 0; // 簡化版本，不追蹤上次點擊時間
    const timeDiff = clickTime - lastClick;
    const DOUBLE_CLICK_DELAY = 300; // 雙擊延遲時間

    if (timeDiff < DOUBLE_CLICK_DELAY) {
      // 雙擊處理
      if (!tabNumberKeys[currentTab][key]?.isActive) {
        // 第一次雙擊：記憶當前時間碼
        console.log(`Setting timestamp for key ${key}`);
        
        setTabNumberKeys(prev => {
          const newState = { ...prev };
          newState[currentTab] = { ...newState[currentTab] };
          const videoCurrentTime = currentTime; // 使用影片的當前播放時間
          newState[currentTab][key] = { 
            keyNo: key,
            isActive: true,
            isABPoint: false,
            isPickUpState: true,
            timestamp: videoCurrentTime,
            videoMode: currentTab,
            frame: 0,
            timecode: formatTimecode(videoCurrentTime, 0),
            note: '',
            screenshot: '',
            flag: 'on',
            playMode: mButtonState
          };
          return newState;
        });
        
        // 顯示成功訊息
        setSnackbarMessage(`已記憶時間碼`);
        setSnackbarOpen(true);
      } else {
        // 第二次雙擊：清除記憶
        console.log(`Clearing timestamp for key ${key}`);
        setTabNumberKeys(prev => {
          const newState = { ...prev };
          newState[currentTab] = { ...newState[currentTab] };
          newState[currentTab][key] = { 
            keyNo: key,
            isActive: false,
            isABPoint: false,
            isPickUpState: false,
            timestamp: null,
            videoMode: null,
            frame: null,
            timecode: '00:00:00 00',
            note: '',
            screenshot: '',
            flag: 'off',
            playMode: 'M'
          };
          return newState;
        });
        
        // 顯示成功訊息
        setSnackbarMessage('已清除時間碼記憶');
        setSnackbarOpen(true);
      }
    } else {
      // 單擊處理
      if (mButtonState === 'M' || mButtonState === 'PB' || mButtonState === 'MS') {
        // M 模式、PB 模式或 MS 模式：跳轉到記憶的時間碼
        const keyState = tabNumberKeys[currentTab][key];
        if (keyState?.isActive && keyState.timestamp !== null) {
          // 如果按鈕已經是藍色狀態，點擊後變為綠色
          if (!keyState.isPickUpState) {
            setTabNumberKeys(prev => {
              const newState = { ...prev };
              newState[currentTab] = { ...newState[currentTab] };
              newState[currentTab][key] = { ...newState[currentTab][key], isPickUpState: true };
              return newState;
            });
          }
          console.log(`Seeking to timestamp for key ${key}: ${keyState.timestamp}`);
          
          // 顯示跳轉訊息
          setSnackbarMessage(`已跳轉至時間碼`);
          setSnackbarOpen(true);
        }
      }
    }
  };

  // 5. 準備 Hook 事件處理器
  const numberKeyHandlers = {
    onNumberKeyClick: handleNumberKeyUp,
    onDeleteModeToggle: () => setIsDeleteMode(!isDeleteMode),
    onSettings: handleSettings,
    onMButtonClick: handleMButtonClick,
    onModeChange: handleModeChange,
    onNoteToggle: handleNoteToggle,
    onCheckClick: handleCheckClick,
    onDeleteButtonClick: handleDeleteButtonClick,
    onDeleteButtonPress: handleDeleteButtonPress,
    onDeleteButtonRelease: handleDeleteButtonRelease,
    onBookmarkClick: handleBookmarkClick,
    onSwitchClick: handleSwitchClick,
    onBButtonClick: handleBButtonClick,
    onSpeedChange: handleSpeedChange,
    onThemeDialogOpen: undefined, // 暫時設為 undefined，稍後處理
    onOpenNoteRecords: () => setNoteRecordsDialogOpen(true),
    onRequestLogin: () => setShowAuthDialog(true)
  };

  // 6. 使用 Hook
  const {
    numberKeys: hookNumberKeys,
    isDeleteMode: hookIsDeleteMode,
    mButtonState: hookMButtonState,
    vbids: hookVbids,
    config: hookConfig,
    handlers: hookHandlers,
    getSButtonStyle: hookGetSButtonStyle,
    setNumberKeys: hookSetNumberKeys,
    setIsDeleteMode: hookSetIsDeleteMode,
    setMButtonState: hookSetMButtonState,
    setVbids: hookSetVbids,
    resetNumberKeys,
    getNumberKeyStats
  } = useMainPageNumberKeys(numberKeyConfig, numberKeyHandlers);

  // 重新排序處理函數
  const handleReorder = useCallback((newOrder: number[]) => {
    console.log('🚀 handleReorder 函數開始執行');
    console.log('🔄 重新排序數字鍵:', newOrder);
    console.log('🔍 當前 numberKeys:', numberKeys);
    
    // 🔧 修復：正確的重新排序邏輯
    // 保持數字鍵的原始編號，只改變它們在對象中的順序
    const reorderedNumberKeys: Record<string, any> = {};
    
    // 按照 newOrder 的順序重新排列 numberKeys
    newOrder.forEach((keyNumber) => {
      const originalKey = keyNumber.toString();
      console.log(`🔍 處理數字鍵 ${keyNumber} (原始鍵: ${originalKey}):`, numberKeys[originalKey]);
      if (numberKeys[originalKey]) {
        // 保持原始的 keyNo，不重新編號
        reorderedNumberKeys[originalKey] = {
          ...numberKeys[originalKey]
        };
        console.log(`✅ 已添加數字鍵 ${keyNumber}:`, reorderedNumberKeys[originalKey]);
      } else {
        console.log(`❌ 數字鍵 ${keyNumber} 不存在於 numberKeys 中`);
      }
    });
    
    // 添加任何沒有在 newOrder 中的數字鍵（保持原位置）
    Object.keys(numberKeys).forEach(key => {
      if (!newOrder.includes(Number(key))) {
        reorderedNumberKeys[key] = numberKeys[key];
        console.log(`🔧 保持原位置的數字鍵 ${key}:`, reorderedNumberKeys[key]);
      }
    });
    
    console.log('🔍 重新排序後的 numberKeys:', reorderedNumberKeys);
    console.log('🔍 重新排序後的 numberKeys 鍵數量:', Object.keys(reorderedNumberKeys).length);
    
    // 同時更新本地狀態和 Hook 狀態
    setNumberKeys(reorderedNumberKeys);
    hookSetNumberKeys(reorderedNumberKeys);
    console.log('✅ 已更新 numberKeys 排序:', reorderedNumberKeys);
    
    // 保存到 Firestore
    if (authUser && mainId) {
      const userDocRef = doc(db, 'users', authUser.uid);
      updateDoc(userDocRef, {
        [`numberKeys.${mainId}`]: reorderedNumberKeys
      }).then(() => {
        console.log('✅ 重新排序已保存到 Firestore');
      }).catch((error) => {
        console.error('❌ 保存重新排序到 Firestore 失敗:', error);
      });
    }
  }, [numberKeys, authUser, mainId, hookSetNumberKeys]);

  // 🔧 修復：移除調試用的 useEffect，避免不必要的性能開銷
  // useEffect(() => {
  //   console.log('AuthDialog rendered, open:', showAuthDialog);
  // }, [showAuthDialog]);

  const handleShowLogin = useCallback(() => {
    setShowRegisterDialog(false);
    setTimeout(() => {
      setShowAuthDialog(true);
    }, 100);
  }, []);

  console.log('mainPageMenuItems (before return):', mainPageMenuItems);

  // 添加 return 前的 debug
  console.log('=== MAINPAGE RETURN DEBUG ===');
  console.log('About to return, mainPageMenuItems:', mainPageMenuItems);
  console.log('isMobile:', isMobile);
  console.log('=== END RETURN DEBUG ===');



  return (
    <ErrorBoundary fallbackRender={({ error }) => (
      <div>發生錯誤: {error.message}</div>
    )}>
      <Suspense fallback={<CircularProgress />}>
        <WelcomeDialog open={showWelcome} onClose={handleCloseWelcome} />
        <div>
          <MainPageComponent 
            handleTitleClick={handleTitleClick}
            currentFirestoreId={currentFirestoreId}
          />
          
          {vbidDialogOpen && (
            <VbidDialog
              open={vbidDialogOpen}
              onClose={handleVbidDialogClose}
              vbids={currentVbids}
              onImport={handleImportVbids}
              currentMode={currentMode || 'YouTube'}
              videoUrl={videoUrl}
              numberKeys={numberKeys}
              abPoints={abPoints}
              currentVbids={currentVbids}
              currentTime={currentTime}
              duration={duration}
              playing={playing}
              volume={volume}
              muted={muted}
              playbackRate={playbackRate}
              played={played}
              loaded={loaded}
              seeking={seeking}
              showControls={showControls}
              showNumberKeyPanel={showNumberKeyPanel}
              showABPointPanel={showABPointPanel}
              showVBIDPanel={showVBIDPanel}
              showImageEditor={showImageEditor}
              imageUrl={imageUrl}
              selectedFontSize={selectedFontSize}
              lineWidthMap={lineWidthMap}
              history={history}
              historyIndex={historyIndex}
              onSave={handleSaveVBIDs}
              onMyFavorite={handleMyFavorite}
              playButtonSound={playButtonSound}
              filterDuplicateVBIDs={filterDuplicateVBIDs}
              setTabVbids={(vbids: VBID[]) => {
                setTabVbids(prev => ({
                  ...prev,
                  [tabValue]: vbids
                }));
              }}
              tabValue={tabValue}
            />
          )}
          {/* 🔧 修復：移除重複的分享視頻標記按鈕，避免在手機版直式顯示時重複出現 */}
          {/* <Button
            variant="contained"
            startIcon={<ShareIcon />}
            onClick={async () => {
              try {
                // 優先使用現有的影片資料，與 ManagePages 方式一致
                let videoForShare = null;
                
                // 如果有 mainId，嘗試從 Firestore 獲取現有影片資料
                if (mainId) {
                  console.log('🔍 嘗試從 Firestore 獲取現有影片資料:', mainId);
                  
                  // 先查詢 sharedVbids 集合
                  const { doc, getDoc } = await import('firebase/firestore');
                  const { db } = await import('../firebase');
                  
                  const sharedVbidsDoc = await getDoc(doc(db, 'sharedVbids', mainId));
                  if (sharedVbidsDoc.exists()) {
                    const existingVideo = sharedVbidsDoc.data();
                    console.log('✅ 找到現有影片資料在 sharedVbids:', existingVideo);
                    videoForShare = {
                      ...existingVideo,
                      id: mainId,
                      vbids: currentVbids, // 使用當前的 vbids
                      lastOpenDate: new Date().toISOString()
                    };
                  } else {
                    // 如果 sharedVbids 沒有，查詢 videos 集合
                    const videosDoc = await getDoc(doc(db, 'videos', mainId));
                    if (videosDoc.exists()) {
                      const existingVideo = videosDoc.data();
                      console.log('✅ 找到現有影片資料在 videos:', existingVideo);
                      videoForShare = {
                        ...existingVideo,
                        id: mainId,
                        vbids: currentVbids, // 使用當前的 vbids
                        lastOpenDate: new Date().toISOString()
                      };
                    }
                  }
                }
                
                // 如果沒有找到現有資料，創建新的影片記錄
                if (!videoForShare) {
                  console.log('🔍 未找到現有影片資料，創建新的影片記錄');
                  
                  // 對於 Google Drive 影片，使用安全的 ID 格式
                  let safeVideoId;
                  let safePath;
                  let safeTitle;
                  
                  if (currentMode === 'Google') {
                    // Google Drive 影片：使用 gdrive_ 前綴 + 檔案 ID
                    const googleDriveFileId = videoUrl.match(/\/d\/([^\/]+)/)?.[1] || mainId?.replace('gdrive_', '') || `gdrive_${Date.now()}`;
                    safeVideoId = `gdrive_${googleDriveFileId}`;
                    safePath = `gdrive://${googleDriveFileId}`; // 使用安全的內部格式
                    safeTitle = `Google Drive 影片 (${googleDriveFileId})`;
                  } else {
                    // YouTube 或其他影片：使用 extractYouTubeId 或 mainId
                    safeVideoId = extractYouTubeId(videoUrl) || mainId || `temp_${Date.now()}`;
                    safePath = videoUrl;
                    safeTitle = videoUrl;
                  }
                  
                  videoForShare = {
                    id: safeVideoId,
                    videoId: safeVideoId,
                    path: safePath,
                    title: safeTitle,
                    vbids: currentVbids,
                    userId: userId,
                    mode: currentMode,
                    mediaMode: currentMode,
                    flag: 'on',
                    addedDate: new Date().toISOString(),
                    lastOpenDate: new Date().toISOString(),
                    zones: ['person'],
                    provider: currentMode === 'Google' ? 'Google Drive' : 'YouTube',
                    checkpointCount: currentVbids?.length || 0,
                    globalCheckpointCount: currentVbids?.length || 0,
                    youtubeInfo: null,
                    videoInfo: null,
                    readUsers: [],
                    favoriteCount: 0,
                    readCount: 0,
                    totalReadCount: 0,
                    hiddenFor: [],
                    groupIds: [],
                    tags: [],
                    modifiedAt: Date.now()
                  };
                }
                
                console.log('🎯 準備分享的影片資料:', videoForShare);
                setCurrentShareVideo(videoForShare);
                setShareDialogOpen(true);
              } catch (error) {
                console.error('❌ 準備分享資料時發生錯誤:', error);
                // 如果出錯，使用簡單的備用方案
                const videoForShare = {
                  id: mainId || `temp_${Date.now()}`,
                  videoId: extractYouTubeId(videoUrl) || mainId || '',
                  path: videoUrl,
                  title: videoUrl,
                  vbids: currentVbids,
                  userId: userId,
                  mode: currentMode,
                  mediaMode: currentMode,
                  flag: 'on',
                  addedDate: new Date().toISOString(),
                  lastOpenDate: new Date().toISOString(),
                  zones: ['person'],
                  provider: 'YouTube',
                  checkpointCount: currentVbids?.length || 0,
                  globalCheckpointCount: currentVbids?.length || 0
                };
                setCurrentShareVideo(videoForShare);
                setShareDialogOpen(true);
              }
            }}
            disabled={!videoUrl}
          >
            分享視頻標記
          </Button> */}
          <UnifiedShareDialog
            open={shareDialogOpen}
            onClose={() => setShareDialogOpen(false)}
            video={currentShareVideo}
            user={authUser}
          />
          {showAuthDialog && (
            <AuthDialog
              open={showAuthDialog}
              onClose={() => setShowAuthDialog(false)}
              onShowRegister={() => {
                setShowAuthDialog(false);
                setShowRegisterDialog(true);
              }}
            />
          )}
          <RegisterDialog
            open={showRegisterDialog}
            onClose={() => setShowRegisterDialog(false)}
            onShowLogin={handleShowLogin}
          />
          <BottomBar menuItems={mainPageMenuItems} />
          <QRCodeDialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} url={shareUrl} />
          {/* 🔧 修復：移除重複的數字鍵面板，避免在手機版直式顯示時重複出現 */}
          {/* 數字鍵面板：每個 Tab 獨立狀態，切換 Tab 時強制 remount */}
          {/* <NumberKeyPanel
            key={tabValue}
            numberKeys={hookNumberKeys}
            isDeleteMode={hookIsDeleteMode}
            mButtonState={hookMButtonState}
            vbids={hookVbids}
            handlers={hookHandlers}
            config={hookConfig}
            getSButtonStyle={hookGetSButtonStyle}
            setVbids={hookSetVbids}
          /> */}
          <NoteRecordsDialog
            open={noteRecordsDialogOpen}
            onClose={() => setNoteRecordsDialogOpen(false)}
            numberKeys={numberKeys}
            onReorder={handleReorder}
            onEditNote={handleEditNote}
            onDeleteNote={handleDeleteNote}
            onTimecodeClick={handleTimecodeClick}
            onCleanupData={handleCleanupData}
            onSaveReorderedData={handleSaveReorderedData}
            currentEditingKey={currentEditingKey}
            currentEditingContent={currentEditingContent}
            onClearTimecode={handleClearTimecode}
          />
        </div>
      </Suspense>
    </ErrorBoundary>
  );
};

export default MainPage;

export function ShortCodeRedirectPage() {
  const { shortCode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snapshot, setSnapshot] = useState<VBIDSnapshot | null>(null);

  useEffect(() => {
    async function fetchSnapshot() {
      setLoading(true);
      setError('');
      try {
        const snapshotId = await getSnapshotIdByShortCode(shortCode!);
        if (!snapshotId) {
          setError('找不到對應的分享內容');
          setLoading(false);
          return;
        }
        const snap = await getVBIDSnapshot(snapshotId);
        if (!snap) {
          setError('快照已被刪除或不存在');
          setLoading(false);
          return;
        }
        setSnapshot(snap);
        setLoading(false);
      } catch (e) {
        setError('載入失敗');
        setLoading(false);
      }
    }
    fetchSnapshot();
  }, [shortCode]);

  if (loading) return <div>載入中...</div>;
  if (error) return <div style={{color:'red'}}>{error}</div>;
  if (!snapshot) return null;

  // 這裡你可以根據 snapshot 資料渲染內容
  return (
    <div>
      <h2>{snapshot.title || '分享內容'}</h2>
      <div>描述：{snapshot.description}</div>
      <pre style={{background:'#eee',padding:8}}>{JSON.stringify(snapshot, null, 2)}</pre>
    </div>
  );
} 